import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import useGetChangeSet from '../../api/useGetChangeSet';
import useReviewChangeSet from '../../api/useReviewChangeSet';
import { PageWrapper, PageContainer } from '../../components/common/PageWrapper.styles';
import { MainContent } from '../../components/common/Layout.styles';
import { PaginationButton } from '../../components/shared/Button.styles';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { ChangelogPage, Error } from './ChangeLogPage.styles';
import { useAuthStore } from '../../context/AuthContext';
import { ChangeSetStatusEnum } from '../../enums/ChangeSetStatusEnum';
import { formatDateTime } from '../../utils/dateFormatters';
import {
    ButtonContainer,
    ContentContainer,
    HeaderSection,
    Column,
    FieldRow,
    FieldLabel,
    ChangesSection,
    SectionHeader,
    SectionContainer,
    ChangeItem,
    ChangeBox,
    ChangeBoxContent,
    BoldText,
    ColoredLabel,
    ChangedFieldContainer,
    ChangedFieldBox,
    ValueList,
    SuccessMessage,
    ErrorMessage,
} from './ChangeSetDetailPage.styles';

interface Difference {
    path: string;
    oldValue: any;
    newValue: any;
    isAdded: boolean;
    isRemoved: boolean;
    isChanged: boolean;
}

const ChangeSetDetailPage = () => {
    const [searchParams] = useSearchParams();
    const changeLogId = searchParams.get('changeLogId');
    const navigate = useNavigate();
    const { changeSet, loading, error, getChangeSet } = useGetChangeSet();
    const { reviewChangeSet, loading: reviewing, error: reviewError, success: reviewSuccess } = useReviewChangeSet();
    const { canAudit } = useAuthStore();
    const [differences, setDifferences] = useState<Difference[]>([]);

    useEffect(() => {
        if (changeLogId) {
            getChangeSet(parseInt(changeLogId, 10));
        }
    }, [changeLogId]);

    // Parse differences from old/new values
    useEffect(() => {
        if (changeSet && (changeSet.oldValues || changeSet.newValues)) {
            try {
                const oldObj = changeSet.oldValues ? JSON.parse(changeSet.oldValues) : {};
                const newObj = changeSet.newValues ? JSON.parse(changeSet.newValues) : {};

                const diffs = calculateDifferences(oldObj, newObj);
                setDifferences(diffs);
            } catch (err) {
                console.error('Error parsing changeset values:', err);
            }
        }
    }, [changeSet?.id, changeSet?.oldValues, changeSet?.newValues]);

    const calculateDifferences = (oldObj: any, newObj: any): Difference[] => {
        const diffs: Difference[] = [];
        const allKeys = new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})]);

        allKeys.forEach((key) => {
            const oldValue = oldObj?.[key];
            const newValue = newObj?.[key];

            if (oldValue === undefined && newValue !== undefined) {
                // Added
                diffs.push({
                    path: key,
                    oldValue: null,
                    newValue,
                    isAdded: true,
                    isRemoved: false,
                    isChanged: false,
                });
            } else if (oldValue !== undefined && newValue === undefined) {
                // Removed
                diffs.push({
                    path: key,
                    oldValue,
                    newValue: null,
                    isAdded: false,
                    isRemoved: true,
                    isChanged: false,
                });
            } else if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
                // Changed
                diffs.push({
                    path: key,
                    oldValue,
                    newValue,
                    isAdded: false,
                    isRemoved: false,
                    isChanged: true,
                });
            }
        });

        return diffs;
    };

    const getPresentationTitle = (path: string): string => {
        // Convert camelCase or PascalCase to Title Case
        return path
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    const renderValue = (value: any, fieldPath?: string): JSX.Element => {
        if (value === null || value === undefined) {
            return <span>N/A</span>;
        }
        if (typeof value === 'boolean') {
            return <span>{value ? 'Yes' : 'No'}</span>;
        }

        // Check if this is a date field and format it
        const dateFields = ['lastupdatedutc', 'lastupdated', 'expirydate', 'datetimeutc', 'reviewdatetimeutc', 'date', 'createdat', 'updatedat'];
        const normalizedFieldPath = fieldPath?.toLowerCase().replace(/\s+/g, '');
        if (normalizedFieldPath && dateFields.some((df) => normalizedFieldPath.endsWith(df))) {
            // Check if value looks like a date string
            if (typeof value === 'string' && !isNaN(Date.parse(value))) {
                return <span>{formatDateTime(value)}</span>;
            }
        }

        // Fields that should be displayed as lists
        const listFields = [
            'temperaturerequirements',
            'physicaldata',
            'gasphysicaldata',
            'regulations:tripartiteagreement',
            'regulations',
            'externalids',
            'procedures',
            'coatingcompatibilities',
            'stowagerules',
            'category',
            'materialsafetydatasheet',
        ];
        const normalizedPath = fieldPath?.toLowerCase().replace(/\s+/g, '');
        const shouldDisplayAsList = normalizedPath && listFields.includes(normalizedPath);

        if (typeof value === 'object' && !Array.isArray(value)) {
            const entries = Object.entries(value);

            // Display as vertical list for specific fields
            if (shouldDisplayAsList) {
                return (
                    <ValueList>
                        {entries.map(([key, val]) => (
                            <li key={key}>
                                <BoldText>{getPresentationTitle(key)}: </BoldText>
                                {typeof val === 'object' ? renderValue(val) : <span>{String(val)}</span>}
                            </li>
                        ))}
                    </ValueList>
                );
            }

            // Render nested object as inline label-value pairs
            return (
                <span>
                    {entries.map(([key, val], idx) => (
                        <span key={key}>
                            <BoldText>{getPresentationTitle(key)}: </BoldText>
                            {typeof val === 'object' ? renderValue(val) : <span>{String(val)}</span>}
                            {idx < entries.length - 1 && <span>, </span>}
                        </span>
                    ))}
                </span>
            );
        }
        if (Array.isArray(value)) {
            return (
                <span>
                    {value.map((item, idx) => (
                        <span key={idx}>
                            {typeof item === 'object' ? renderValue(item) : <span>{String(item)}</span>}
                            {idx < value.length - 1 && <span>, </span>}
                        </span>
                    ))}
                </span>
            );
        }
        return <span>{String(value)}</span>;
    };

    const handleBack = () => {
        navigate('/changelog');
    };

    const handleConfirmReview = async () => {
        if (!changeSet) return;

        try {
            await reviewChangeSet(changeSet);
            // Refresh the changeset data after successful review
            if (changeLogId) {
                getChangeSet(parseInt(changeLogId, 10));
            }
        } catch (err) {
            // Error is already handled by useReviewChangeSet
            console.error('Failed to confirm review:', err);
        }
    };

    const canConfirmReview = () => {
        return canAudit && changeSet?.status === ChangeSetStatusEnum.PendingReview && !reviewing;
    };

    if (loading) {
        return (
            <PageWrapper>
                <PageContainer>
                    <ChangelogPage as={MainContent}>
                        <LoadingSpinner text="Loading changeset details..." />
                    </ChangelogPage>
                </PageContainer>
            </PageWrapper>
        );
    }

    if (error) {
        return (
            <PageWrapper>
                <PageContainer>
                    <ChangelogPage as={MainContent}>
                        <Error>Error loading changeset: {error}</Error>
                        <PaginationButton onClick={handleBack} style={{ marginTop: '1rem' }}>
                            <FontAwesomeIcon icon={faChevronLeft} /> Back to list
                        </PaginationButton>
                    </ChangelogPage>
                </PageContainer>
            </PageWrapper>
        );
    }

    if (!changeSet) {
        return (
            <PageWrapper>
                <PageContainer>
                    <ChangelogPage as={MainContent}>
                        <Error>Changeset not found</Error>
                        <PaginationButton onClick={handleBack} style={{ marginTop: '1rem' }}>
                            <FontAwesomeIcon icon={faChevronLeft} /> Back to list
                        </PaginationButton>
                    </ChangelogPage>
                </PageContainer>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <PageContainer>
                <ChangelogPage as={MainContent}>
                    <ButtonContainer>
                        <PaginationButton onClick={handleBack}>
                            <FontAwesomeIcon icon={faChevronLeft} /> Back to list
                        </PaginationButton>
                        {canAudit && (
                            <PaginationButton onClick={handleConfirmReview} disabled={!canConfirmReview()}>
                                Confirm Review
                            </PaginationButton>
                        )}
                    </ButtonContainer>

                    {/* Success/Error Messages */}
                    {reviewing && <LoadingSpinner text="Saving change set information..." />}
                    {reviewSuccess && (
                        <SuccessMessage>
                            <strong>Success!</strong> Change set information saved.
                        </SuccessMessage>
                    )}
                    {reviewError && (
                        <ErrorMessage>
                            <strong>Error!</strong> {reviewError}
                        </ErrorMessage>
                    )}

                    <ContentContainer>
                        <HeaderSection>
                            {/* Left Column */}
                            <Column>
                                <FieldRow>
                                    <FieldLabel>Category:</FieldLabel>
                                    <span>{changeSet.categoryName}</span>
                                </FieldRow>
                                <FieldRow>
                                    <FieldLabel>Item:</FieldLabel>
                                    <span>{changeSet.displayInformation}</span>
                                </FieldRow>
                                <FieldRow>
                                    <FieldLabel>User:</FieldLabel>
                                    <span>{changeSet.userName}</span>
                                </FieldRow>
                                <FieldRow>
                                    <FieldLabel>Time of Change:</FieldLabel>
                                    <span>{formatDateTime(changeSet.date)}</span>
                                </FieldRow>
                            </Column>

                            {/* Right Column */}
                            <Column>
                                <FieldRow>
                                    <FieldLabel>Type:</FieldLabel>
                                    <span>{changeSet.typeName}</span>
                                </FieldRow>
                                <FieldRow>
                                    <FieldLabel>Status:</FieldLabel>
                                    <span>{changeSet.statusName}</span>
                                </FieldRow>
                                {changeSet.reviewerName && (
                                    <>
                                        <FieldRow>
                                            <FieldLabel>Reviewer:</FieldLabel>
                                            <span>{changeSet.reviewerName}</span>
                                        </FieldRow>
                                        <FieldRow>
                                            <FieldLabel>Time of Review:</FieldLabel>
                                            <span>{changeSet.reviewDate ? formatDateTime(changeSet.reviewDate) : 'N/A'}</span>
                                        </FieldRow>
                                    </>
                                )}
                            </Column>
                        </HeaderSection>

                        <hr />

                        {/* Changes Section */}
                        <ChangesSection>
                            {differences.length > 0 ? (
                                <>
                                    {/* Removed Fields */}
                                    {differences.filter((d) => d.isRemoved).length > 0 && (
                                        <SectionContainer>
                                            <SectionHeader color="#a94442">Removed Fields</SectionHeader>
                                            {differences
                                                .filter((d) => d.isRemoved)
                                                .map((diff, index) => (
                                                    <ChangeItem key={index}>
                                                        <ChangeBox variant="removed">
                                                            <ChangeBoxContent>
                                                                <BoldText>{getPresentationTitle(diff.path)}: </BoldText>
                                                                {renderValue(diff.oldValue, diff.path)}
                                                            </ChangeBoxContent>
                                                        </ChangeBox>
                                                    </ChangeItem>
                                                ))}
                                        </SectionContainer>
                                    )}

                                    {/* Added Fields */}
                                    {differences.filter((d) => d.isAdded).length > 0 && (
                                        <SectionContainer withPadding>
                                            <SectionHeader color="#3c763d" marginLeft="1rem">
                                                Added Fields
                                            </SectionHeader>
                                            {differences
                                                .filter((d) => d.isAdded)
                                                .map((diff, index) => (
                                                    <ChangeItem key={index}>
                                                        <ChangeBox variant="added">
                                                            <ChangeBoxContent>
                                                                <BoldText>{getPresentationTitle(diff.path)}: </BoldText>
                                                                {renderValue(diff.newValue, diff.path)}
                                                            </ChangeBoxContent>
                                                        </ChangeBox>
                                                    </ChangeItem>
                                                ))}
                                        </SectionContainer>
                                    )}

                                    {/* Changed Fields */}
                                    {differences.filter((d) => d.isChanged).length > 0 && (
                                        <SectionContainer>
                                            <SectionHeader>Changed Fields</SectionHeader>
                                            {differences
                                                .filter((d) => d.isChanged)
                                                .map((diff, index) => (
                                                    <ChangeItem key={index}>
                                                        <ChangedFieldContainer>
                                                            <ChangedFieldBox variant="removed">
                                                                <ColoredLabel color="#a94442">Changed From: </ColoredLabel>
                                                                <ChangeBoxContent>
                                                                    <BoldText>{getPresentationTitle(diff.path)}: </BoldText>
                                                                    {renderValue(diff.oldValue, diff.path)}
                                                                </ChangeBoxContent>
                                                            </ChangedFieldBox>
                                                            <ChangedFieldBox variant="added">
                                                                <ColoredLabel color="#3c763d">Changed To: </ColoredLabel>
                                                                <ChangeBoxContent>
                                                                    <BoldText>{getPresentationTitle(diff.path)}: </BoldText>
                                                                    {renderValue(diff.newValue, diff.path)}
                                                                </ChangeBoxContent>
                                                            </ChangedFieldBox>
                                                        </ChangedFieldContainer>
                                                    </ChangeItem>
                                                ))}
                                        </SectionContainer>
                                    )}
                                </>
                            ) : (
                                <div>No changes detected</div>
                            )}
                        </ChangesSection>
                    </ContentContainer>
                </ChangelogPage>
            </PageContainer>
        </PageWrapper>
    );
};

export default ChangeSetDetailPage;
